import { useEffect, useState, type ReactNode } from "react";
import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined";
import {
  Alert,
  AlertTitle,
  Button,
  FormControlLabel,
  Link,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import type { Promisable } from "type-fest";

import { dayjs } from "@thalamos/common";
import type {
  ListingDateRangeFilterUnbranded,
  ListingUnassignedCount,
  PersonDirectoryEntry,
  PersonDirectoryPage,
  PersonListingFacets,
  PersonListingFilters,
  PersonListingFiltersUnbranded,
} from "@thalamos/haven-common";
import {
  calendarDayOf,
  customerRolesOf,
  DIRECTORY_PAGE_LIMIT_DEFAULT,
  displayName,
  involvementKindsHeldByUser,
  isPartyActingAsOrganisationAdmin,
  LISTING_TICK_LIMIT,
  mayActInPersonDirectory,
  membershipLabel,
  PersonInvolvementKindSchema,
} from "@thalamos/haven-common";

import { apiHooks } from "../../api/index.js";
import {
  DateRangeFilter,
  FilterChips,
  OptionsFilter,
  type FilterChip,
} from "../../components/ListingFilters/ListingFilters.js";
import { useUser } from "../../Context/UserContext.js";
import { createRoute } from "../../routing/createRoute.js";
import { DirectoryFilter } from "../Admin/DirectoryFilter.js";
import {
  DirectoryTable,
  type DirectoryColumn,
  type DirectoryColumnFilter,
  type DirectorySort,
} from "../Admin/DirectoryTable.js";
import { involvementLabelOf } from "./involvementLabels.js";
import {
  usePersonListing,
  type PersonListingColumn,
} from "./usePersonListing.js";

/**
 * Why the listing cannot be shown.
 *
 * `not_permitted` is the server refusing the session context the session acts
 * in — a state the page has to explain rather than render as an empty caseload.
 * Everything else is `unavailable`.
 */
export type ListingFailure = "not_permitted" | "unavailable";

/** Read off the status rather than the error — see `validateStatus` in `api/axios/api.ts`. */
export const listingFailure = (
  status: number | undefined,
  errored: boolean,
): ListingFailure | undefined => {
  if (status === 200) {
    return undefined;
  }

  if (status === 403) {
    return "not_permitted";
  }

  return errored || status !== undefined ? "unavailable" : undefined;
};

const asDate = (value: Date | string): string =>
  dayjs(value).format("DD/MM/YYYY");

// An approval is an instant, and the filter counts it on the calendar day it
// fell on, so it is shown on that day too.
const asDayOf = (instant: Date | string): string => {
  const day = calendarDayOf(instant);

  return day === undefined ? "" : asDate(day);
};

/**
 * What is missing from a profile the user can act on, or nothing: the warning
 * is for whoever runs the case, or for an admin of the body administering it.
 */
const unassignedWarningOf = (
  entry: PersonDirectoryEntry,
  administering: boolean,
  t: TFunction,
): string | undefined =>
  entry.state.kind === "unassigned" &&
  (administering
    ? entry.administered
    : involvementKindsHeldByUser(entry.routes).includes("responsible_user"))
    ? t("personListing.unassigned", {
        missing: entry.state.missing
          .map((kind) => t(`access.kind.${kind}`))
          .join(", "),
      })
    : undefined;

/**
 * Every holder's name the counts have given, so a chip keeps naming one the
 * latest counts no longer offer.
 */
const useNamesSeen = (
  facets: PersonListingFacets | undefined,
): ReadonlyMap<string, string> => {
  const [names, setNames] = useState(() => namesIn(facets));
  const [counted, setCounted] = useState(facets);

  if (facets !== counted) {
    setCounted(facets);
    setNames(new Map([...names, ...namesIn(facets)]));
  }

  return names;
};

const namesIn = (
  facets: PersonListingFacets | undefined,
): ReadonlyMap<string, string> =>
  new Map<string, string>(
    facets === undefined
      ? []
      : [
          ...[
            ...facets.dutyOfCare.organisations,
            ...facets.placement.organisations,
          ].map(({ organisationId, name }) => [organisationId, name] as const),
          ...facets.caseManager.users.map(
            ({ userId, name }) => [userId, name] as const,
          ),
        ],
  );

/** Keeps the last value that arrived, so a refetch does not blank what is shown. */
const useLastArrived = <T,>(value: T | undefined): T | undefined => {
  const [kept, setKept] = useState(value);

  useEffect(() => {
    if (value !== undefined) {
      setKept(value);
    }
  }, [value]);

  return value ?? kept;
};

/** The young people the session reaches. */
export const PersonListingPage = () => {
  // Follows the directory of the organisation the session acts for, which is
  // the same scope the server lists within.
  const { user } = useUser();
  const organisationId = user?.sessionContext?.organisationId;
  const administering = isPartyActingAsOrganisationAdmin({
    roles: customerRolesOf(user?.sessionContext?.roles ?? []),
  });
  const listing = usePersonListing({ administering });

  const [{ data, loading, error, response }] = apiHooks.person.search(
    listing.request,
    organisationId,
  );

  // An admin's count covers everything it administers, which costs more than
  // the page, so it is asked for on its own.
  const [{ data: administered, response: administeredResponse }] =
    apiHooks.person.search(listing.unassignedCountRequest, organisationId, {
      manual: !administering,
    });

  // The counts beside each filter's options cost a pass over the whole reach,
  // so they are only asked for once a panel has been opened.
  const [countsWanted, setCountsWanted] = useState(false);
  const [{ data: counted, response: countedResponse }] = apiHooks.person.search(
    listing.countsRequest,
    organisationId,
    { manual: !countsWanted },
  );

  const page = response?.status === 200 ? data : undefined;
  const facets = useLastArrived(
    countedResponse?.status === 200 ? counted?.facets : undefined,
  );
  const unassigned = useLastArrived(
    administering
      ? administeredResponse?.status === 200
        ? administered?.unassigned
        : undefined
      : page?.unassigned,
  );

  const { onlyUnassigned, setOnlyUnassigned } = listing;
  // A toggle that stops being offered while on would narrow the list unseen.
  useEffect(() => {
    if (onlyUnassigned && unassigned !== undefined && !unassigned.offered) {
      setOnlyUnassigned(false);
    }
  }, [onlyUnassigned, unassigned, setOnlyUnassigned]);

  return (
    <PersonListingPageContent
      page={page}
      loading={loading}
      failure={listingFailure(
        response?.status,
        error !== undefined && error !== null,
      )}
      filter={listing.input}
      filtered={listing.narrowed}
      onFilterChange={listing.setQuery}
      mayMatch={mayActInPersonDirectory(user?.sessionContext ?? null, "match")}
      actingOrganisationName={
        user?.sessionContext === undefined || user.sessionContext === null
          ? undefined
          : membershipLabel(user.sessionContext)
      }
      offset={listing.offset}
      onPageChange={listing.setOffset}
      sort={listing.sort}
      onSortChange={listing.setSort}
      filters={listing.filters}
      onFilterSet={listing.setFilter}
      onFiltersClear={listing.clearFilters}
      facets={facets}
      onCountsWanted={() => {
        setCountsWanted(true);
      }}
      administering={administering}
      unassigned={unassigned}
      onlyUnassigned={onlyUnassigned}
      onOnlyUnassignedChange={setOnlyUnassigned}
    />
  );
};

type FilterSetter = <Column extends keyof PersonListingFiltersUnbranded>(
  column: Column,
  value: PersonListingFiltersUnbranded[Column] | undefined,
) => Promisable<void>;

const dateRangeOf = (
  value: ListingDateRangeFilterUnbranded | undefined,
): ListingDateRangeFilterUnbranded | undefined =>
  value === undefined ||
  (value.from === null && value.to === null && !value.includeUnknown)
    ? undefined
    : value;

/** A filter left keeping nothing would list nobody, so it is dropped instead. */
const unlessEmpty = <T,>({
  kept,
  includeOther,
  value,
}: {
  kept: readonly string[];
  includeOther: boolean;
  value: T;
}): T | undefined => (kept.length === 0 && !includeOther ? undefined : value);

/**
 * One chip per value a filter keeps, named by its column, each taking only
 * its own value out of the filter.
 */
const chipsOf = ({
  filters,
  names,
  actingOrganisationName,
  onFilterSet,
  t,
}: {
  filters: PersonListingFilters;
  names: ReadonlyMap<string, string>;
  actingOrganisationName: string | undefined;
  onFilterSet: FilterSetter;
  t: TFunction;
}): FilterChip[] => {
  const chip = ({
    column,
    labelKey,
    valueId,
    value,
    onRemove,
  }: {
    column: keyof PersonListingFilters;
    labelKey: string;
    valueId: string;
    value: string;
    onRemove: () => Promisable<void>;
  }): FilterChip => ({
    key: `${column}:${valueId}`,
    label: t("listingFilters.chip", { column: t(labelKey), value }),
    onRemove,
  });

  const rangeChips = ({
    column,
    labelKey,
    unknownKey,
  }: {
    column: "dateOfBirth" | "lastApproved";
    labelKey: string;
    unknownKey: string;
  }): FilterChip[] => {
    const filter = filters[column];

    if (filter === undefined) {
      return [];
    }

    const { from, to, includeUnknown } = filter;
    const dates =
      from !== null && to !== null
        ? t("listingFilters.rangeBoth", { from: asDate(from), to: asDate(to) })
        : from !== null
          ? t("listingFilters.rangeFrom", { from: asDate(from) })
          : to !== null
            ? t("listingFilters.rangeTo", { to: asDate(to) })
            : undefined;

    return [
      ...(dates === undefined
        ? []
        : [
            chip({
              column,
              labelKey,
              valueId: "range",
              value: dates,
              onRemove: () =>
                onFilterSet(
                  column,
                  dateRangeOf({ from: null, to: null, includeUnknown }),
                ),
            }),
          ]),
      ...(includeUnknown
        ? [
            chip({
              column,
              labelKey,
              valueId: "unknown",
              value: t(unknownKey),
              onRemove: () =>
                onFilterSet(
                  column,
                  dateRangeOf({ from, to, includeUnknown: false }),
                ),
            }),
          ]
        : []),
    ];
  };

  const bodyChips = (facet: "dutyOfCare" | "placement"): FilterChip[] => {
    const filter = filters[facet];

    if (filter === undefined) {
      return [];
    }

    const { organisationIds, includeNotAssigned } = filter;
    const labelKey = `personListing.${facet}`;

    return [
      ...organisationIds.map((id) => {
        const kept = organisationIds.filter((other) => other !== id);

        return chip({
          column: facet,
          labelKey,
          valueId: id,
          value: names.get(id) ?? id,
          onRemove: () =>
            onFilterSet(
              facet,
              unlessEmpty({
                kept,
                includeOther: includeNotAssigned,
                value: {
                  organisationIds: kept,
                  includeNotAssigned,
                },
              }),
            ),
        });
      }),
      ...(includeNotAssigned
        ? [
            chip({
              column: facet,
              labelKey,
              valueId: "notAssigned",
              value: t("personListing.notAssigned"),
              onRemove: () =>
                onFilterSet(
                  facet,
                  unlessEmpty({
                    kept: organisationIds,
                    includeOther: false,
                    value: {
                      organisationIds,
                      includeNotAssigned: false,
                    },
                  }),
                ),
            }),
          ]
        : []),
    ];
  };

  const involvementChips = (): FilterChip[] => {
    const { involvement } = filters;

    if (involvement === undefined) {
      return [];
    }

    const { kinds, includeViewerThroughOrganisation } = involvement;
    const labelKey = "personListing.yourInvolvement";

    return [
      ...kinds.map((kind) => {
        const kept = kinds.filter((other) => other !== kind);

        return chip({
          column: "involvement",
          labelKey,
          valueId: kind,
          value: t(`access.kind.${kind}`),
          onRemove: () =>
            onFilterSet(
              "involvement",
              unlessEmpty({
                kept,
                includeOther: includeViewerThroughOrganisation,
                value: {
                  kinds: kept,
                  includeViewerThroughOrganisation,
                },
              }),
            ),
        });
      }),
      ...(includeViewerThroughOrganisation
        ? [
            chip({
              column: "involvement",
              labelKey,
              valueId: "viewerThrough",
              value: t("personListing.viewerThrough", {
                organisation: actingOrganisationName,
              }),
              onRemove: () =>
                onFilterSet(
                  "involvement",
                  unlessEmpty({
                    kept: kinds,
                    includeOther: false,
                    value: {
                      kinds,
                      includeViewerThroughOrganisation: false,
                    },
                  }),
                ),
            }),
          ]
        : []),
    ];
  };

  const caseManagerChips = (): FilterChip[] => {
    const { caseManager } = filters;

    if (caseManager === undefined) {
      return [];
    }

    const { userIds, includeNotAssigned } = caseManager;
    const labelKey = "personListing.caseManager";

    return [
      ...userIds.map((id) => {
        const kept = userIds.filter((other) => other !== id);

        return chip({
          column: "caseManager",
          labelKey,
          valueId: id,
          value: names.get(id) ?? id,
          onRemove: () =>
            onFilterSet(
              "caseManager",
              unlessEmpty({
                kept,
                includeOther: includeNotAssigned,
                value: {
                  userIds: kept,
                  includeNotAssigned,
                },
              }),
            ),
        });
      }),
      ...(includeNotAssigned
        ? [
            chip({
              column: "caseManager",
              labelKey,
              valueId: "notAssigned",
              value: t("personListing.notAssigned"),
              onRemove: () =>
                onFilterSet(
                  "caseManager",
                  unlessEmpty({
                    kept: userIds,
                    includeOther: false,
                    value: {
                      userIds,
                      includeNotAssigned: false,
                    },
                  }),
                ),
            }),
          ]
        : []),
    ];
  };

  return [
    ...rangeChips({
      column: "dateOfBirth",
      labelKey: "personListing.dateOfBirth",
      unknownKey: "personListing.notKnown",
    }),
    ...bodyChips("dutyOfCare"),
    ...bodyChips("placement"),
    ...involvementChips(),
    ...caseManagerChips(),
    ...rangeChips({
      column: "lastApproved",
      labelKey: "personListing.lastApproved",
      unknownKey: "personListing.none",
    }),
  ];
};

/**
 * The page's rendering, with the listing and its controls injected so a story or
 * a test can show any state it has without an API client.
 */
export const PersonListingPageContent = ({
  page,
  loading,
  failure,
  filter,
  filtered,
  onFilterChange,
  mayMatch,
  offset,
  onPageChange,
  actingOrganisationName,
  sort,
  onSortChange,
  filters,
  onFilterSet,
  onFiltersClear,
  facets,
  onCountsWanted,
  administering,
  unassigned,
  onlyUnassigned,
  onOnlyUnassignedChange,
}: {
  page: PersonDirectoryPage | undefined;
  loading: boolean;
  failure: ListingFailure | undefined;
  filter: string;
  /** Whether the listing shown was narrowed, by search, filter or toggle. */
  filtered: boolean;
  onFilterChange: (value: string) => void;
  mayMatch: boolean;
  offset: number;
  onPageChange: (offset: number) => void;
  /** Who the user views a young person through when they hold no involvement. */
  actingOrganisationName: string | undefined;
  sort: DirectorySort<PersonListingColumn> | null;
  onSortChange: (
    sort: DirectorySort<PersonListingColumn> | null,
  ) => Promisable<void>;
  filters: PersonListingFilters;
  onFilterSet: FilterSetter;
  onFiltersClear: () => Promisable<void>;
  /** The counts beside each filter's options, once asked for. */
  facets: PersonListingFacets | undefined;
  onCountsWanted: () => Promisable<void>;
  /**
   * Whether the session is an admin's: shown who runs each case in place of
   * its own part, and warned of the unassigned profiles its organisation
   * administers.
   */
  administering: boolean;
  /** The toggle, when the listing offers one. */
  unassigned: ListingUnassignedCount | undefined;
  onlyUnassigned: boolean;
  onOnlyUnassignedChange: (only: boolean) => Promisable<void>;
}) => {
  const { t } = useTranslation();
  const names = useNamesSeen(facets);

  const nobodyInReach = !filtered && page?.total === 0;

  const bodies = (facet: "dutyOfCare" | "placement") => {
    const value = filters[facet];

    return (
      <OptionsFilter
        searchable
        maxSelected={LISTING_TICK_LIMIT}
        options={(facets?.[facet].organisations ?? []).map((organisation) => ({
          id: organisation.organisationId,
          label: organisation.name,
          count: organisation.count,
        }))}
        other={{
          label: t("personListing.notAssigned"),
          count: facets?.[facet].notAssigned ?? 0,
        }}
        value={{
          selected: value?.organisationIds ?? [],
          includeOther: value?.includeNotAssigned ?? false,
        }}
        onChange={(next) =>
          onFilterSet(
            facet,
            next === undefined ||
              (next.selected.length === 0 && !next.includeOther)
              ? undefined
              : {
                  organisationIds: next.selected,
                  includeNotAssigned: next.includeOther,
                },
          )
        }
      />
    );
  };

  const filterOf = (
    column: keyof PersonListingFilters,
    panel: ReactNode,
  ): DirectoryColumnFilter => ({
    active: filters[column] !== undefined,
    panel,
    onOpen: onCountsWanted,
  });

  const involvementColumn: DirectoryColumn<
    PersonDirectoryEntry,
    PersonListingColumn
  > = {
    id: "involvement",
    labelKey: "personListing.yourInvolvement",
    sortable: true,
    filter: filterOf(
      "involvement",
      <OptionsFilter
        options={(facets?.involvement.kinds ?? []).map(({ kind, count }) => ({
          id: kind,
          label: t(`access.kind.${kind}`),
          count,
        }))}
        other={{
          label: t("personListing.viewerThrough", {
            organisation: actingOrganisationName,
          }),
          count: facets?.involvement.viewerThroughOrganisation ?? 0,
        }}
        value={{
          selected: filters.involvement?.kinds ?? [],
          includeOther:
            filters.involvement?.includeViewerThroughOrganisation ?? false,
        }}
        onChange={(next) =>
          onFilterSet(
            "involvement",
            next === undefined ||
              (next.selected.length === 0 && !next.includeOther)
              ? undefined
              : {
                  kinds: next.selected.flatMap((id) => {
                    const kind = PersonInvolvementKindSchema.safeParse(id);

                    return kind.success ? [kind.data] : [];
                  }),
                  includeViewerThroughOrganisation: next.includeOther,
                },
          )
        }
      />,
    ),
    render: (entry) =>
      involvementLabelOf(entry.routes, actingOrganisationName, t),
  };

  const lastApprovedColumn: DirectoryColumn<
    PersonDirectoryEntry,
    PersonListingColumn
  > = {
    id: "lastApproved",
    labelKey: "personListing.lastApproved",
    sortable: true,
    filter: filterOf(
      "lastApproved",
      <DateRangeFilter
        value={filters.lastApproved}
        unknown={{
          label: t("personListing.none"),
          count: facets?.lastApproved.none ?? 0,
        }}
        onChange={(next) => onFilterSet("lastApproved", dateRangeOf(next))}
      />,
    ),
    render: (entry) =>
      entry.lastApprovedAt === null
        ? t("personListing.none")
        : asDayOf(entry.lastApprovedAt),
  };

  const caseManagerColumn: DirectoryColumn<
    PersonDirectoryEntry,
    PersonListingColumn
  > = {
    id: "caseManager",
    labelKey: "personListing.caseManager",
    sortable: true,
    filter: filterOf(
      "caseManager",
      <OptionsFilter
        searchable
        maxSelected={LISTING_TICK_LIMIT}
        options={(facets?.caseManager.users ?? []).map((user) => ({
          id: user.userId,
          label: user.name,
          count: user.count,
        }))}
        other={{
          label: t("personListing.notAssigned"),
          count: facets?.caseManager.notAssigned ?? 0,
        }}
        value={{
          selected: filters.caseManager?.userIds ?? [],
          includeOther: filters.caseManager?.includeNotAssigned ?? false,
        }}
        onChange={(next) =>
          onFilterSet(
            "caseManager",
            next === undefined ||
              (next.selected.length === 0 && !next.includeOther)
              ? undefined
              : {
                  userIds: next.selected,
                  includeNotAssigned: next.includeOther,
                },
          )
        }
      />,
    ),
    render: (entry) =>
      entry.caseManager?.name ?? t("personListing.notAssigned"),
  };

  // The name is the link rather than a separate action column: the row is the
  // record, so the record's name is what opens it.
  const columns: DirectoryColumn<PersonDirectoryEntry, PersonListingColumn>[] =
    [
      {
        id: "name",
        labelKey: "personListing.name",
        sortable: true,
        render: (entry) => {
          const warning = unassignedWarningOf(entry, administering, t);

          return (
            <Stack direction="row" spacing={1} alignItems="center">
              <Link
                component={RouterLink}
                to={createRoute.person(entry.id)}
                sx={{ whiteSpace: "nowrap" }}
              >
                {displayName(entry.names)}
              </Link>
              {warning === undefined ? null : (
                <Tooltip title={warning}>
                  {/* Focusable so the warning opens from the keyboard as well as on hover. */}
                  <span
                    tabIndex={0}
                    role="img"
                    aria-label={warning}
                    style={{ display: "inline-flex" }}
                  >
                    <WarningAmberOutlined color="warning" fontSize="small" />
                  </span>
                </Tooltip>
              )}
            </Stack>
          );
        },
      },
      {
        id: "upn",
        labelKey: "personListing.upn",
        render: (entry) => entry.upn ?? t("personListing.notKnown"),
      },
      {
        id: "dateOfBirth",
        labelKey: "personListing.dateOfBirth",
        sortable: true,
        filter: filterOf(
          "dateOfBirth",
          <DateRangeFilter
            value={filters.dateOfBirth}
            unknown={{
              label: t("personListing.notKnown"),
              count: facets?.dateOfBirth.unknown ?? 0,
            }}
            onChange={(next) => onFilterSet("dateOfBirth", dateRangeOf(next))}
          />,
        ),
        render: (entry) =>
          entry.dateOfBirth === null
            ? t("personListing.notKnown")
            : asDate(entry.dateOfBirth),
      },
      {
        id: "dutyOfCare",
        labelKey: "personListing.dutyOfCare",
        sortable: true,
        filter: filterOf("dutyOfCare", bodies("dutyOfCare")),
        render: (entry) =>
          entry.dutyOfCare?.name ?? t("personListing.notAssigned"),
      },
      {
        id: "placement",
        labelKey: "personListing.placement",
        sortable: true,
        filter: filterOf("placement", bodies("placement")),
        render: (entry) =>
          entry.placement?.name ?? t("personListing.notAssigned"),
      },
      // An admin is shown who runs each case in place of its own part, and no
      // approval date, because an admin's list shows nothing of an assessment.
      ...(administering
        ? [caseManagerColumn]
        : [involvementColumn, lastApprovedColumn]),
    ];

  return (
    <Stack spacing={2} sx={{ maxWidth: 1100 }}>
      <Stack
        direction="row"
        spacing={2}
        alignItems="flex-start"
        justifyContent="space-between"
      >
        <Stack spacing={0.5}>
          <Typography variant="h4" component="h1">
            {t("personListing.title")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("personListing.description")}
          </Typography>
        </Stack>
        {mayMatch ? (
          <Button
            component={RouterLink}
            to={createRoute.matchPerson()}
            variant="contained"
          >
            {t("personListing.searchOrAdd")}
          </Button>
        ) : null}
      </Stack>

      {failure === "not_permitted" ? (
        <Alert severity="info">{t("personListing.notPermitted")}</Alert>
      ) : (
        <>
          <Stack direction="row" spacing={2} alignItems="center">
            <DirectoryFilter
              value={filter}
              label={t("personListing.filter")}
              onChange={onFilterChange}
            />
            {unassigned?.offered === true ? (
              <FormControlLabel
                control={
                  <Switch
                    checked={onlyUnassigned}
                    onChange={(event) => {
                      void onOnlyUnassignedChange(event.target.checked);
                    }}
                  />
                }
                label={t("personListing.onlyUnassigned", {
                  count: unassigned.count,
                })}
              />
            ) : null}
          </Stack>

          <FilterChips
            chips={chipsOf({
              filters,
              names,
              actingOrganisationName,
              onFilterSet,
              t,
            })}
            onClearAll={onFiltersClear}
          />

          {nobodyInReach ? (
            <Alert severity="info">
              <AlertTitle>{t("personListing.empty.title")}</AlertTitle>
              {mayMatch ? t("personListing.empty.body") : null}
            </Alert>
          ) : (
            <DirectoryTable
              rows={page?.results ?? []}
              total={page?.total ?? 0}
              loading={loading}
              error={failure === "unavailable"}
              columns={columns}
              rowKey={(entry) => entry.id}
              limit={DIRECTORY_PAGE_LIMIT_DEFAULT}
              offset={offset}
              onPageChange={onPageChange}
              sort={sort}
              onSortChange={onSortChange}
            />
          )}
        </>
      )}
    </Stack>
  );
};
